/**
 * Converts specified value to a number representation.
 * Note that '#FAA' is not equal to '0xFAA' ('0xFAA' -> '#000FAA').
 *
 * @param value Either a string (# or 0x) or a number.
 */
export const colorToNumber = (value: string | number) => {
  if (typeof value === 'string') {
    if (!isValidColorHex(value)) {
      throw new Error(`Not a valid color hex value: ${value}`);
    }

    // Hash color (#)    => Convert to 6-digit hex then to number.
    // Zero x color (0x) => Convert to number.
    return parseInt(
      value.startsWith('#') ? expandCssHexAbbreviation(stripHexColorPrefix(value)) : value,
      16,
    );
  }

  // Validate number color.
  if (!isValidColorNumber(value)) {
    throw new Error(`Value is not a valid color number: ${value}`);
  }

  // Value is number, no conversion needed.
  return value;
};

/**
 * Returns true if value is between 0x0 and 0xFFFFFFFF (with alpha).
 *
 * @param value Value to validate.
 */
const isValidColorNumber = (value: number) => {
  return value >= 0 && value <= parseInt('0xFFFFFFFF', 16);
};

/**
 * Returns a value indicating whether the specified value is a valid color hex.
 * Valid values are: '#RGB', '#RGBA', '#RRGGBB', '#RRGGBBAA' and 0x0 - 0xFFFFFFFF.
 */
const isValidColorHex = (value: string) => {
  if (typeof value !== 'string') {
    return false;
  }

  // Check CSS color formats.
  if (value.startsWith('#')) {
    return /^([0-9A-F]{3}|[0-9A-F]{4}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(stripHexColorPrefix(value));
  }

  // Check number range.
  if (value.startsWith('0x')) {
    return isValidColorNumber(parseInt(stripHexColorPrefix(value), 16));
  }

  return false;
};

/**
 * Returns a new string with leading '#' or '0x' removed.
 */
const stripHexColorPrefix = (value: string): string => {
  if (typeof value === 'string') {
    if (value.startsWith('#')) {
      return value.substring(1);
    }

    if (value.startsWith('0x')) {
      return value.substring(2);
    }
  }

  // Make sure to return a string.
  return value.toString();
};

/**
 * Returns a new hex string converted to a six digit representation.
 *
 * @param value Value to convert.
 */
const expandCssHexAbbreviation = (hex: string) => {
  // Expand 3-digit hex to 6-digit (CSS abbreviation to full form).
  return hex.length === 3 ? hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2] : hex;
};
